import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import type { SightingType, SightingStatus, DetectionSource, SightingFilters as SightingFiltersType } from "../types";
import type { SelectChangeEvent } from "@mui/material";

interface SightingFiltersProps {
  filters: SightingFiltersType;
  onFiltersChange: (filters: SightingFiltersType) => void;
  totalCount: number;
  filteredCount: number;
}

const typeOptions: { value: SightingType; label: string }[] = [
  { value: "earth_moving", label: "Earth Moving" },
  { value: "road_works", label: "Road Works" },
  { value: "building_works", label: "Building Works" },
  { value: "machinery_parked", label: "Machinery" },
  { value: "agi_activity", label: "AGI Activity" },
  { value: "marker_posts", label: "Marker Post" },
  { value: "forestry", label: "Forestry" },
  { value: "quarry", label: "Quarry" },
  { value: "flooding", label: "Flooding" },
  { value: "ditching", label: "Ditching" },
  { value: "network_works", label: "Network Works" },
  { value: "fencing", label: "Fencing" },
  { value: "other", label: "Other" },
  { value: "unidentified", label: "Unidentified" },
];

const statusOptions: { value: SightingStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "dispatched", label: "Dispatched" },
  { value: "closed", label: "Closed" },
];

const sourceOptions: { value: DetectionSource; label: string }[] = [
  { value: "node", label: "Node" },
  { value: "helicopter", label: "Helicopter" },
  { value: "manual", label: "Manual" },
  { value: "third_party", label: "Third Party" },
];

export const SightingFilters = ({ filters, onFiltersChange, totalCount, filteredCount }: SightingFiltersProps) => {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, search: event.target.value });
  };

  const handleTypeChange = (event: SelectChangeEvent<SightingType[]>) => {
    const value = event.target.value;
    onFiltersChange({ ...filters, types: typeof value === "string" ? value.split(",") as SightingType[] : value });
  };

  const handleStatusChange = (event: SelectChangeEvent<SightingStatus[]>) => {
    const value = event.target.value;
    onFiltersChange({ ...filters, statuses: typeof value === "string" ? value.split(",") as SightingStatus[] : value });
  };

  const handleSourceChange = (event: SelectChangeEvent<DetectionSource[]>) => {
    const value = event.target.value;
    onFiltersChange({ ...filters, sources: typeof value === "string" ? value.split(",") as DetectionSource[] : value });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      types: [],
      statuses: [],
      sources: [],
      zones: [],
      dateRange: { start: null, end: null },
    });
  };

  const hasActiveFilters = filters.search || filters.types.length > 0 || filters.statuses.length > 0 || filters.sources.length > 0;

  const renderValue = (selected: string[], options: { value: string; label: string }[]) => {
    if (selected.length === 0) return "";
    if (selected.length === 1) {
      return options.find(o => o.value === selected[0])?.label || selected[0];
    }
    return `${selected.length} selected`;
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            placeholder="Search sightings..."
            size="small"
            value={filters.search}
            onChange={handleSearchChange}
            sx={{ minWidth: 200, flex: "0 1 300px" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              multiple
              value={filters.types}
              onChange={handleTypeChange}
              input={<OutlinedInput label="Type" />}
              renderValue={(selected) => renderValue(selected, typeOptions)}
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.types.includes(option.value)} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={filters.statuses}
              onChange={handleStatusChange}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => renderValue(selected, statusOptions)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.statuses.includes(option.value)} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Source</InputLabel>
            <Select
              multiple
              value={filters.sources}
              onChange={handleSourceChange}
              input={<OutlinedInput label="Source" />}
              renderValue={(selected) => renderValue(selected, sourceOptions)}
            >
              {sourceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.sources.includes(option.value)} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clearFilters}
              deleteIcon={<CloseIcon />}
              variant="outlined"
            />
          )}

          <Box sx={{ flex: 1 }} />

          <Typography variant="body2" color="text.secondary">
            {filteredCount} of {totalCount}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};
